// shared/src/tickers.ts
// Phase 5 Step 20.C — canonical active ticker set + asset-class predicate.
//
// Phase 12.2.E.pre lock (2026-05-20): `$`-prefix is the canonical syntactic
// discriminator for crypto in D1. Migration `0004_phase-12.2-ticker-prefix.sql`
// migrated existing signals.ticker + daily_closes.ticker rows to add `$` for
// the 4 observed crypto tickers (BTC, ETH, SOL, AZTEC). CANONICAL_TICKERS
// reflects the new convention; CRYPTO_TICKERS Set is the membership oracle.
//
// The two `isCryptoTicker` implementations (this file + the wider local one
// previously at workers/refresh-fetcher/src/lib/fetch-helpers.ts:105) are now
// consolidated to this single source — refresh-fetcher imports from here.
// CRYPTO_TICKERS expanded from {BTC,ETH} to {BTC,ETH,SOL,AZTEC,XRP,DOGE} to
// cover both PROD inventory (SOL, AZTEC) and the previously-wider
// refresh-fetcher knowledge (XRP, DOGE — anticipated extensions). Set keys
// stay BARE so `isCryptoTicker` can tolerate both input forms ('$BTC' / 'BTC')
// via the strip-then-lookup pattern. The CANONICAL FORM for D1 storage is
// `$`-prefixed (lock above).

// Session 5T D22 (2026-05-28) — added '$AZTEC' to canonical set. PROD has
// emitted V3 signals for $AZTEC since 2026-05-26 (Phase 12.2.E pre-lock
// expanded CRYPTO_TICKERS Set to include AZTEC, but CANONICAL_TICKERS was
// never bumped to match — D7 verification on /signal-history hard-crashed
// the ticker dropdown). Per [[feedback-writer-ddl-lockstep]]: all signals
// surfacing routes + dashboard ticker enums must accept '$AZTEC' or the
// brief's portfolio ticker list silently drops it.
export const CANONICAL_TICKERS = ['PDBC', 'AGNC', 'SPY', '$BTC', '$ETH', '$AZTEC'] as const;
export type CanonicalTicker = (typeof CANONICAL_TICKERS)[number];

// Session 5T D44 (2026-05-28) — added 'ADA' after the D32 restore of
// CONTEXT_TICKERS pulled $ADA back into the active set, but the Set was
// never expanded. Symptom: refresh-fetcher routed $ADA to Polygon (stock)
// because isCryptoTicker('$ADA') returned false → Polygon returned "no
// results", $ADA had no crypto-side coverage at all.
export const CRYPTO_TICKERS: ReadonlySet<string> = new Set(['BTC', 'ETH', 'SOL', 'AZTEC', 'XRP', 'DOGE', 'ADA']);

// 2026-07-15 — the `$`-PREFIX now WINS, ahead of the Set. Per the Phase-12.2.E
// lock in this file's header, `$` IS the canonical syntactic discriminator for
// crypto; CRYPTO_TICKERS exists only to ALSO tolerate the BARE vendor/LLM form
// ('BTC'), never to gate the canonical one. Set-only membership made a novel
// $-ticker classify as EQUITY — the oracle's holdings keys ($WBTC/$WSTETH/$LIT/
// $WORMSOL) landed in the dashboard's "Stocks & ETFs" basket (reported
// 2026-07-15), and the same read drives format-price + vendor routing.
//
// This mirrors what universe/validate.ts detectAssetClass has done correctly all
// along ("$-prefix wins … lets a novel ticker like $AVAX be recognized as crypto
// even though it's not in CRYPTO_TICKERS") and what sparkline-pivot.ts hand-patched
// locally (`startsWith('$') || isCryptoTicker(…)`). Both were compensating for THIS
// function's gap; the rule belongs here, in the shared membership oracle.
//
// A BARE symbol still requires Set membership — 'WBTC' alone is ambiguous with an
// equity ticker, so bare-form tolerance stays an explicit allowlist.
export function isCryptoTicker(ticker: string): boolean {
  if (ticker.startsWith('$')) return true;
  return CRYPTO_TICKERS.has(ticker.toUpperCase());
}

// World-state Phase 2 (2026-05-26, #80) — asset-class classification beyond
// crypto-vs-not. Used by world-state-projection to derive
// WorldStateHeadlineObservable.asset_class so the UI primitive can pick
// formatters (dollar-prefix vs plain), and by formatPrice helpers.
//
// The "macro" set covers indicators that are not USD-quoted prices: yields
// (TNX, DGS10), volatility indices (VIX, MOVE, VVIX), dominance ratios
// (BTC.DOM, ETH.DOM), sentiment scalars (FEAR_GREED, SKEW). Bare-alpha
// equity/ETF tickers (SPY, QQQ, XLK, GLD, AGNC, PDBC, NVDA, etc.) fall
// through to 'equity'.
//
// The macro set is the inverse-defined membership: known macro indicators.
// Anything bare-alpha NOT in this set is treated as an equity/ETF.
export const MACRO_INDICATORS: ReadonlySet<string> = new Set([
  // Yields / rates
  'VIX', 'MOVE', 'VVIX', 'SKEW', 'VIX_TS',
  'TNX', 'TYX', 'DGS10', 'DGS2', 'DGS30',
  'YIELD_SPREAD_2S10S', 'BREAKEVEN_INFLATION_10Y', 'TIPS_10Y_REAL_YIELD',
  'HY_SPREAD_BPS', 'FED_FUNDS_RATE',
  // FX / Dollar
  'DXY', 'EURUSD', 'USDJPY', 'GBPUSD',
  // Crypto dominance / regime
  'BTC.DOM', 'ETH.DOM',
  // Sentiment / index scalars
  'FEAR_GREED',
]);

// #55 (2026-08-25) — the 4 macro INDEX tickers whose daily_closes value is resolved from
// oracle_macro_series (FRED/CMC), NOT oracle_canonical price. These are the keys of
// daily-close-worker's MACRO_INDEX_FIELD map (VIX→vix, DXY→dxy, TNX→ten_year_yield,
// TYX→yield_30y) — kept here as the SHARED, drift-proof membership set so the settled-close
// heal (planHeal) and the DC-03 coverage invariant can both EXCLUDE them from the price roster
// without re-typing a hand-list. They have no oracle_canonical price row, so readCanonicalAtDate
// cannot source them — v1 heal is price-roster-only; macro-index heal is a v2 deferral (#55).
// MUST stay in lockstep with daily-close-worker/src/scheduled.ts MACRO_INDEX_FIELD's keys.
export const MACRO_INDEX_TICKERS: ReadonlySet<string> = new Set(['VIX', 'DXY', 'TNX', 'TYX']);

export type AssetClass = 'crypto' | 'equity' | 'macro';

/** Asset-class classifier used by world-state projection + price formatter.
 *  Order matters: crypto-prefix wins, then known-macro set, then bare-alpha
 *  falls through to equity (the broadest catch-all). */
export function classifyAssetClass(ticker: string): AssetClass {
  if (isCryptoTicker(ticker)) return 'crypto';
  if (MACRO_INDICATORS.has(ticker.toUpperCase())) return 'macro';
  return 'equity';
}

// S-31 (2026-07-10) — 5-BUCKET asset-class classifier for the macro-prediction
// cross-asset diversity gate (v2-market-write `MIN_ASSET_CLASSES_PER_PREDICTION`).
// `classifyAssetClass`'s 3-way read lumps rates + FX + vol + sentiment into a single
// 'macro' bucket, AND leaves bare-alpha commodity ETFs (GLD/USO/PDBC) as 'equity' — so a
// genuine SPY+TNX+GLD prediction collapsed to ONE class and was wrongly dropped (F9a-11).
// This split matches the m-synthesis rule-12 "5 categories": equity / rates / commodity /
// FX / crypto. Sourced from MACRO_INDICATORS' own "Yields/rates" + "FX/Dollar" blocks.
export type AssetBucket = 'crypto' | 'rates' | 'fx' | 'commodity' | 'equity';

// Govt yields, curve/credit spreads, policy rate, bond-vol (MOVE), + the duration bond ETFs.
const RATES_INDICATORS: ReadonlySet<string> = new Set([
  'TNX', 'TYX', 'DGS10', 'DGS2', 'DGS30', 'YIELD_SPREAD_2S10S',
  'BREAKEVEN_INFLATION_10Y', 'TIPS_10Y_REAL_YIELD', 'HY_SPREAD_BPS',
  'FED_FUNDS_RATE', 'MOVE', 'TLT', 'IEF',
]);

// The dollar index + major USD pairs (bare + Yahoo '=X' forms both handled by the strip).
const FX_INDICATORS: ReadonlySet<string> = new Set([
  'DXY', 'EURUSD', 'USDJPY', 'GBPUSD', 'AUDUSD', 'USDCAD', 'USDCHF', 'NZDUSD',
]);

// Commodity ETFs/proxies — BARE-ALPHA (not in MACRO_INDICATORS) so classifyAssetClass
// leaves them 'equity'; the 5-bucket read pulls them into their own class.
export const COMMODITY_TICKERS: ReadonlySet<string> = new Set([
  'GLD', 'IAU', 'USO', 'USL', 'PDBC', 'DBC', 'SLV', 'UNG', 'DBA', 'CPER',
]);

/** 5-bucket asset-class classifier (S-31). Order: crypto (incl. BTC.DOM/ETH.DOM
 *  dominance) → rates → fx → commodity → equity (catch-all: equity ETFs/stocks +
 *  equity-vol VIX/VVIX/SKEW + sentiment FEAR_GREED). Tolerates a '$' prefix and Yahoo
 *  '^' index / '=X' FX suffixes so both canonical and vendor forms classify alike. */
export function classifyAssetBucket(ticker: string): AssetBucket {
  const t = ticker.trim().toUpperCase().replace(/^\^/, '').replace(/=X$/, '');
  if (isCryptoTicker(ticker) || t === 'BTC.DOM' || t === 'ETH.DOM') return 'crypto';
  if (RATES_INDICATORS.has(t)) return 'rates';
  if (FX_INDICATORS.has(t)) return 'fx';
  if (COMMODITY_TICKERS.has(t)) return 'commodity';
  return 'equity';
}
